import { EVIDENCE_ENFORCEMENT_LABELS, EvidenceEnforcement } from './evidence.enforcement';
import { isEvidenceExplained } from './evidence.exception.type';
import { Task } from './task';
import { TaskLinkType, TaskTarget } from './task.enum';
import { isEvidenceSectionCaptured, resolveEvidenceConfig, TASK_REQUIREMENT_FLAG_FIELDS, TaskEvidenceKind } from './task.evidence';
import { isStampedTaskCompleted, isStampedTaskResolved } from './task.status.policy';

export interface TaskEvidenceShortfall {
    kind: TaskEvidenceKind;
    level: EvidenceEnforcement;
}

/** The status name is the tenant's own, never fixed wording. */
export interface SubTaskSummary {
    taskId: string;
    name: string;
    statusName: string;
}

/** Its own missing evidence travels with it, so an authorised user sees exactly what completing it with its parent would override; absent when the caller may not read that sub-task. */
export interface OpenSubTask extends SubTaskSummary {
    missingEvidence?: TaskEvidenceShortfall[];
}

/** The gate always names the task and its resolved sub-tasks; a refusal built for another question, such as Request SOR, need not. */
export interface TaskCompletionRefusal {
    taskName?: string;
    missingEvidence: TaskEvidenceShortfall[];
    openSubTasks: OpenSubTask[];
    resolvedSubTasks?: SubTaskSummary[];
}

export const TASK_COMPLETION_BLOCKED_CODE = 'TaskCompletionBlocked';

/** Read-time projections ($lookup, aggregation, computed) that Mongo never stores, so a client sending one must not change what the gate judges (a fabricated `formResponseEntity.completed` passed a draft form). */
const TASK_PROJECTION_ONLY_FIELDS = ['formResponseEntity', 'childProgress', 'effectiveSharing', 'isReadonly'] as const satisfies readonly (keyof Task)[];

function withoutTaskProjectionFields(patch: Partial<Task>): Partial<Task> {

    return Object.fromEntries(Object.entries(patch ?? {}).filter(([field]) => !TASK_PROJECTION_ONLY_FIELDS.some(projected => projected === field)));
}

// The requirement and the daily-template marker decide what is checked, so for a stored task they are read from the store; a request that also lowered them would otherwise judge itself against its own weaker rule.
const JUDGED_FROM_STORED_FIELDS = [...TASK_REQUIREMENT_FLAG_FIELDS, 'daily', 'instance'] as const satisfies readonly (keyof Task)[];

export function buildJudgedTask(before: Task | null | undefined, patch: Partial<Task>): Partial<Task> {

    // `formCompleted` is a plain task field any earlier PATCH can have stored, so only the server-loaded response or a form file proves the form; only the stored exception explains evidence.
    const merged: Partial<Task> = { ...before, ...withoutTaskProjectionFields(patch), formCompleted: undefined, evidenceException: before?.evidenceException };
    if (!before) return merged;

    return { ...merged, ...Object.fromEntries(JUDGED_FROM_STORED_FIELDS.map(field => [field, before[field]])) };
}

/** The response id inside the old field app's `formResponse` link token, or undefined for anything that is not one. */
export function readFormResponseTokenId(token: string | null | undefined): string | undefined {

    if (!token) return undefined;

    try {
        const link: unknown = JSON.parse(atob(token));
        return link && typeof link === 'object' && 'rId' in link && typeof link.rId === 'string' ? link.rId : undefined;
    } catch {
        return undefined;
    }
}

/** A dotted `taskStatus.*` key reaches Mongo's `$set` as is, yet a shallow merge never sees it, so a stamp can only be written whole. */
export function findPartialStatusKey(patch: object | null | undefined): string | undefined {

    return Object.keys(patch ?? {}).find(field => field.startsWith('taskStatus.'));
}

/** The refusal message and the Web App dialogs name a section the same way. */
export const TASK_EVIDENCE_SECTION_LABELS: Record<TaskEvidenceKind, string> = {
    beforePhoto: 'Before photos',
    duringPhoto: 'During photos',
    afterPhoto: 'After photos',
    form: 'Form',
    file: 'Files',
    signature: 'Signature',
    comment: 'Comment',
};

/** Only a move into Completed is gated: Not Required and Cannot Complete are how a task whose evidence cannot be captured gets closed. */
export function isMoveIntoCompleted(before: Pick<Task, 'status' | 'taskStatus'> | null | undefined, after: Pick<Task, 'status' | 'taskStatus'>): boolean {

    return isStampedTaskCompleted(after) && !isStampedTaskCompleted(before);
}

/** Judged on the task as the write would leave it, so evidence sent with the completion counts; lists what is missing whether or not an exception explains it. */
export function findMissingTaskEvidence(task: Partial<Task>): TaskEvidenceShortfall[] {

    return resolveEvidenceConfig(task)
        .filter(config => config.enforcement !== EvidenceEnforcement.Optional)
        .filter(config => !isEvidenceSectionCaptured(task, config.kind, true))
        .map(config => ({ kind: config.kind, level: config.enforcement }));
}

/** The missing evidence that still holds the task: the task's own exception explains Required (allow exceptions) of the types it was recorded for, never Required (no exceptions). */
export function findBlockingTaskEvidence(task: Partial<Task>): TaskEvidenceShortfall[] {

    return findMissingTaskEvidence(task).filter(shortfall => !isEvidenceExplained(task.evidenceException, shortfall));
}

/** A caller who reads every sub-task of a task it may complete; the portal and the field app read fewer. */
export type SubTaskReadable = (child: Task) => boolean;

const READS_EVERY_SUB_TASK: SubTaskReadable = () => true;

/** The completion gate on the task as the write would leave it; an override passes missing evidence only, never an open sub-task. Every child decides, but only a readable one discloses its evidence or its resolved status. */
export function findTaskCompletionRefusal(task: Partial<Task>, children: readonly Task[] | null | undefined, overrideEvidence = false, canRead: SubTaskReadable = READS_EVERY_SUB_TASK): TaskCompletionRefusal | undefined {

    const missingEvidence = findBlockingTaskEvidence(task);
    const openSubTasks: OpenSubTask[] = selectOpenSubTasks(task, children).map(child => canRead(child) ? { ...summariseSubTask(child), missingEvidence: findBlockingTaskEvidence(child) } : summariseSubTask(child));
    const resolvedSubTasks = selectCountedSubTasks(task, children).filter(child => isStampedTaskResolved(child) && canRead(child)).map(summariseSubTask);

    return openSubTasks.length || (missingEvidence.length && !overrideEvidence)
        ? { taskName: task.name ?? task.description ?? '', missingEvidence, openSubTasks, resolvedSubTasks }
        : undefined;
}

/** Plain text that stands alone, because older field apps show only the message of a 400; the subject lets a SOR request refuse in the same words. */
export function describeTaskCompletionRefusal(refusal: TaskCompletionRefusal, subject = 'Task cannot be completed'): string {

    return [
        refusal.missingEvidence.length ? `${subject} until this evidence is captured: ${refusal.missingEvidence.map(describeTaskEvidenceShortfall).join(', ')}` : '',
        refusal.openSubTasks.length ? `${subject} while these sub-tasks are open: ${refusal.openSubTasks.map(subTask => `${subTask.name} (${subTask.statusName})`).join(', ')}` : '',
    ]
        .filter(Boolean)
        .join('. ');
}

/** Unable to capture is offered only when some missing evidence allows an exception; the refused evidence is what still blocks, so any such row is unexplained. */
export function describeMissingEvidenceGuidance(missingEvidence: readonly TaskEvidenceShortfall[]): string {

    return missingEvidence.some(shortfall => shortfall.level === EvidenceEnforcement.RequiredExceptionAllowed)
        ? 'Add the missing evidence, record Unable to capture on the task, or close it with a different status.'
        : 'Add the missing evidence, or close the task with a different status.';
}

export function describeTaskEvidenceShortfall(shortfall: TaskEvidenceShortfall): string {

    return `${TASK_EVIDENCE_SECTION_LABELS[shortfall.kind]} (${EVIDENCE_ENFORCEMENT_LABELS[shortfall.level]})`;
}

// Item Task Compliance sub-tasks hang off the SOR's item task as Compliance, and the completion gate holds the SOR on them too.
export function isSorSubTask(task: Pick<Task, 'type'> | null | undefined): boolean {

    return task?.type === TaskLinkType.ItemTask || task?.type === TaskLinkType.Compliance;
}

/** The sub-tasks that hold their parent open; completing a parent with its sub-tasks completes exactly these. */
export function selectOpenSubTasks(parent: Pick<Task, 'daily' | 'instance'>, children: readonly Task[] | null | undefined): Task[] {

    return selectCountedSubTasks(parent, children).filter(child => !isStampedTaskResolved(child));
}

// A daily template is only a stamp for its dated instances, which are days of the same task, not sub-tasks; a switched-off child never holds its parent.
// A SOR's Office/Supervisor sub-task is the office's own check, which the contractor can neither see nor resolve, so it never holds the SOR (WBS-12380).
function selectCountedSubTasks(parent: Pick<Task, 'daily' | 'instance'>, children: readonly Task[] | null | undefined): Task[] {

    if (parent.daily && !parent.instance) return [];

    return (children ?? [])
        .filter(child => child.active !== false)
        .filter(child => !(child.daily && child.instance))
        .filter(child => !isSorSubTask(child) || child.target === TaskTarget.Field);
}

function summariseSubTask(child: Task): SubTaskSummary {

    return { taskId: child._id, name: child.name ?? child.description ?? '', statusName: child.taskStatus?.name ?? child.status ?? '' };
}
